import { tokenUtil } from "./TokenUtil";
import { clearAuthTokens } from "./authResume";

// Broker portal tokens. The broker bearer token is short-lived (minutes), so a
// refresh token (issued at login by /auth/broker/login) is used to silently mint
// a new bearer on 401 instead of forcing the broker admin to log in again.
const BROKER_PROFILE_STORAGE_KEY = "BrokerProfile";
const BEARER_TOKEN_KEY = "BearerToken";
const REFRESH_TOKEN_KEY = "RefreshToken";
const TOKEN_EXPIRY_KEY = "TokenExpiry";
const LEGACY_BROKER_REFRESH_TOKEN_KEY = "BrokerRefreshToken";

const parseBrokerProfile = () => {
  try {
    return JSON.parse(localStorage.getItem(BROKER_PROFILE_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const getProfileValue = (source = {}, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== "") {
      return source[key];
    }
  }
  return "";
};

export const isBrokerSession = () => {
  const profile = parseBrokerProfile();
  return profile?.role === "broker_admin";
};

export const getBrokerRefreshToken = () =>
  localStorage.getItem(REFRESH_TOKEN_KEY)
  || localStorage.getItem(LEGACY_BROKER_REFRESH_TOKEN_KEY)
  || "";

// The broker logo moved from a plain `logo` string to a `logoAttachment`
// Attachment object ({ urls: [{ key }] }). Resolve a displayable URL from either
// shape (also tolerates the application-form `brokerLogoAttachment` decoration).
export const brokerLogoSrc = (source = {}) => {
  if (!source) return "";
  const attachment = source.logoAttachment || source.brokerLogoAttachment;
  const url = attachment?.urls?.[0];
  return url?.key || url?.translatedKey || source.logo || source.brokerLogo || "";
};

export const normalizeBrokerProfile = (profile = {}, email = "", existing = {}) => {
  const userContent = profile?.userContent || profile || {};
  const brokerId = getProfileValue(userContent, "brokerId", "broker_id") || existing.brokerId || existing.broker_id || "";
  const brokerAdminId = getProfileValue(userContent, "brokerAdminId", "brokerAdmin_id")
    || existing.brokerAdminId
    || existing.brokerAdmin_id
    || "";
  const primaryValue = getProfileValue(userContent, "isPrimaryAdmin");
  const isPrimaryAdmin = primaryValue === ""
    ? Boolean(existing.isPrimaryAdmin)
    : primaryValue === true || primaryValue === "true" || primaryValue === 1 || primaryValue === "1";

  return {
    ...existing,
    ...userContent,
    brokerId,
    broker_id: brokerId,
    brokerAdminId,
    brokerAdmin_id: brokerAdminId,
    isPrimaryAdmin,
    canManageTeam: isPrimaryAdmin,
    role: userContent.role || existing.role || "broker_admin",
    email: email || userContent.email || existing.email || "",
    brokerName: userContent.brokerName || userContent.displayName || existing.brokerName || "",
    logo: brokerLogoSrc(userContent) || existing.logo || "",
  };
};

export const persistBrokerAuthResponse = (data = {}, { email = "" } = {}) => {
  const existing = parseBrokerProfile() || {};
  const nextProfile = normalizeBrokerProfile(data?.userContent || data, email || existing.email, existing);

  localStorage.setItem(BROKER_PROFILE_STORAGE_KEY, JSON.stringify(nextProfile));

  if (data?.bearerToken) {
    localStorage.setItem(BEARER_TOKEN_KEY, data.bearerToken);
  }

  if (data?.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  }

  if (data?.expiry) {
    localStorage.setItem(TOKEN_EXPIRY_KEY, data.expiry);
  }

  localStorage.removeItem(LEGACY_BROKER_REFRESH_TOKEN_KEY);
  return nextProfile;
};

export const clearBrokerAuthStorage = () => {
  clearAuthTokens();
  localStorage.removeItem(BROKER_PROFILE_STORAGE_KEY);
  localStorage.removeItem(LEGACY_BROKER_REFRESH_TOKEN_KEY);
};

const readBearer = (data = {}) => (
  data.bearerToken
  || data.token
  || data?.userContent?.bearerToken
  || ""
);

// Dedupe concurrent refreshes: many requests can 401 at once, so they all await
// the same in-flight refresh rather than hammering the endpoint.
let refreshInFlight = null;

export const refreshBrokerBearerToken = async (failedToken = "") => {
  if (!isBrokerSession()) return false;
  const currentBearer = localStorage.getItem(BEARER_TOKEN_KEY) || "";
  if (failedToken && currentBearer && failedToken !== currentBearer) return true;

  const refreshToken = getBrokerRefreshToken();
  if (!refreshToken) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await tokenUtil.callAPI({
        reqParam: {},
        method: "GET",
        endPoint: "/auth/broker/refresh-token",
        token: refreshToken,
      });
      if (response?.status !== 200) return false;
      const data = response?.data || {};
      const bearer = readBearer(data);
      if (!bearer) return false;
      persistBrokerAuthResponse(data);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};
